/**
 * FctConfigProvider — wrapper de ConfigProvider de Ant Design 5.x con la
 * configuración corporativa de Farmaconsulting Transacciones.
 *
 * Envolver la app completa con este componente para que todos los componentes
 * de AntD hereden el theme corporativo.
 *
 * Uso:
 *   import { FctConfigProvider } from './FctConfigProvider';
 *
 *   ReactDOM.createRoot(document.getElementById('root')!).render(
 *     <FctConfigProvider>
 *       <App />
 *     </FctConfigProvider>
 *   );
 */

import React, { useEffect } from 'react';
import { ConfigProvider } from 'antd';
import esES from 'antd/locale/es_ES';
import 'dayjs/locale/es';
import dayjs from 'dayjs';
import { fctTheme } from './theme';

dayjs.locale('es');

interface FctConfigProviderProps {
  children: React.ReactNode;
  /**
   * Si se pasa en `true` (por defecto), inyecta Roboto desde Google Fonts.
   * Si la app ya carga Roboto por su cuenta, pasar `false` para evitar
   * duplicados.
   */
  loadRobotoFont?: boolean;
}

export const FctConfigProvider: React.FC<FctConfigProviderProps> = ({
  children,
  loadRobotoFont = true,
}) => {
  useEffect(() => {
    if (!loadRobotoFont) return;
    // Evitamos duplicar el <link> si ya existe
    const existing = document.querySelector(
      'link[data-fct-roboto="true"]',
    );
    if (existing) return;

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href =
      'https://fonts.googleapis.com/css2?' +
      'family=Roboto:wght@300;400;500;700&display=swap';
    link.setAttribute('data-fct-roboto', 'true');
    document.head.appendChild(link);
  }, [loadRobotoFont]);

  return (
    <ConfigProvider locale={esES} theme={fctTheme}>
      {children}
    </ConfigProvider>
  );
};
